import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth, normalizeUser } from '../auth/authContext';
import { clearAdminImpersonationBackup } from '../utils/adminImpersonationStorage';
import {
  sendLoginOtp,
  sendResetPasswordOtp,
  confirmResetPassword,
  verifyLogin,
  loginWithPassword,
  setStoredTokens,
  updateRoleSwitchPin,
  normalizeLoginPhone,
} from '../services/authApi';
import { normalizeLocalPhoneDigits } from '../utils/uzPhone';
import { useWebOtp } from '../hooks/useWebOtp';
import './LoginModal.css';

const PHONE_PREFIX = '+998';
const OTP_RESEND_COOLDOWN = 60;
const OTP_CODE_LENGTH = 4;

/** Desktop landing: rasmiy Adde logotipi — to'liq brand SVG (emblem + wordmark) */
function LoginHeroBrandLogo() {
  return (
    <div className="login-modal-page-logo-wrap">
      <img
        className="login-modal-page-logo-full"
        src="/adde-logo-dark.svg"
        alt="Adde"
        draggable="false"
      />
    </div>
  );
}

function formatPhoneWithSpaces(digits) {
  if (!digits.length) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  if (digits.length <= 7) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
}

function OtpInput({
  value,
  onChange,
  disabled,
  id,
  placeholder,
  webOtpActive,
  onComplete,
}) {
  const completedRef = useRef(false);
  const inputRef = useRef(null);
  const captureRef = useRef(null);

  const applyCode = useCallback(
    (raw) => {
      const digits = String(raw ?? '').replace(/\D/g, '').slice(0, OTP_CODE_LENGTH);
      onChange(digits);
      if (digits.length === OTP_CODE_LENGTH && onComplete && !completedRef.current) {
        completedRef.current = true;
        onComplete(digits);
      }
      if (digits.length < OTP_CODE_LENGTH) {
        completedRef.current = false;
      }
      return digits;
    },
    [onChange, onComplete],
  );

  useWebOtp({
    enabled: Boolean(webOtpActive) && !disabled,
    codeLength: OTP_CODE_LENGTH,
    onCode: applyCode,
  });

  useEffect(() => {
    if (!webOtpActive || disabled) return undefined;
    const focusOtp = () => {
      captureRef.current?.focus({ preventScroll: true });
      inputRef.current?.focus({ preventScroll: true });
    };
    const id1 = window.requestAnimationFrame(focusOtp);
    const id2 = window.setTimeout(focusOtp, 350);
    return () => {
      window.cancelAnimationFrame(id1);
      window.clearTimeout(id2);
    };
  }, [webOtpActive, disabled]);

  useEffect(() => {
    if (value.length === OTP_CODE_LENGTH && onComplete && !completedRef.current) {
      completedRef.current = true;
      onComplete(value);
      return;
    }
    if (value.length < OTP_CODE_LENGTH) {
      completedRef.current = false;
    }
  }, [value, onComplete]);

  return (
    <form
      className="login-modal-otp-form"
      autoComplete="on"
      onSubmit={(e) => e.preventDefault()}
    >
      <input
        ref={captureRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        name="one-time-code"
        className="login-modal-otp-autofill-capture"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        onInput={(e) => applyCode(e.currentTarget.value)}
      />
      <input
        ref={inputRef}
        id={id}
        name="one-time-code-visible"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={OTP_CODE_LENGTH}
        className="login-modal-otp-input"
        placeholder={placeholder || '0000'}
        value={value}
        onInput={(e) => applyCode(e.currentTarget.value)}
        onChange={(e) => applyCode(e.target.value)}
        autoComplete="one-time-code"
        disabled={disabled}
        autoFocus
        enterKeyHint="done"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />
    </form>
  );
}

function ResendTimer({ cooldown, onResend, loading, label, labelTimer }) {
  const [seconds, setSeconds] = useState(cooldown);

  useEffect(() => {
    setSeconds(cooldown);
  }, [cooldown]);

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [seconds]);

  if (seconds > 0) {
    return (
      <span className="login-modal-resend-timer">
        {(labelTimer || 'Qayta yuborish ({{n}} s)').replace('{{n}}', seconds)}
      </span>
    );
  }

  return (
    <button
      type="button"
      className="login-modal-resend-btn"
      onClick={() => { setSeconds(OTP_RESEND_COOLDOWN); onResend(); }}
      disabled={loading}
    >
      {label || 'Qayta yuborish'}
    </button>
  );
}

const LoginModal = ({ isOpen, onClose, mode = 'modal' }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { setUser, setEffectiveRole } = useAuth();
  const isPageMode = mode === 'page';

  // ─── State ───────────────────────────────────────────────────────────────
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP (2FA login)
  const [loginOtp, setLoginOtp] = useState('');

  // Password reset
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // PIN step (existing)
  const [pinVal, setPinVal] = useState('');
  const [pinConfirmVal, setPinConfirmVal] = useState('');
  const [pinError, setPinError] = useState('');

  // Resend cooldown key (changing it resets the timer)
  const [resendKey, setResendKey] = useState(0);

  // ─── Reset on close ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen && !isPageMode) {
      setStep('phone');
      setPhone('');
      setPassword('');
      setError('');
      setLoading(false);
      setLoginOtp('');
      setResetOtp('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setResetSuccess(false);
      setPinVal('');
      setPinConfirmVal('');
      setPinError('');
      setResendKey(0);
    }
  }, [isOpen, isPageMode]);

  useEffect(() => {
    if (isPageMode) return;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, isPageMode]);

  // iOS Safari visual viewport
  useEffect(() => {
    if (!isOpen && !isPageMode) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      document.documentElement.style.setProperty('--login-vv-height', `${vv.height}px`);
      document.documentElement.style.setProperty('--login-vv-width', `${vv.width}px`);
      document.documentElement.style.setProperty('--login-vv-offset-top', `${vv.offsetTop}px`);
      document.documentElement.style.setProperty('--login-vv-offset-left', `${vv.offsetLeft}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      ['--login-vv-height', '--login-vv-width', '--login-vv-offset-top', '--login-vv-offset-left'].forEach(
        (k) => document.documentElement.style.removeProperty(k),
      );
    };
  }, [isOpen, isPageMode]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const normalizedPhone = phone.replace(/\D/g, '');
  const phoneDigits = normalizedPhone.length >= 9 ? normalizedPhone.slice(0, 9) : normalizedPhone;
  const fullPhone = normalizeLocalPhoneDigits(phoneDigits) ?? '';

  const handlePhoneChange = (e) => {
    setPhone(e.target.value.replace(/\D/g, '').slice(0, 9));
    setError('');
  };

  const finalizeLogin = useCallback(
    ({ token, accessToken, refreshToken, sessionId, user }) => {
      if (accessToken && refreshToken) {
        setStoredTokens({ accessToken, refreshToken, sessionId });
      } else if (token) {
        setStoredTokens({ accessToken: token, refreshToken: null, sessionId });
      }
      if (!user) {
        // Backend must return profile payload; never assume a privileged role.
        setStoredTokens({ accessToken: null, refreshToken: null, sessionId: null });
        setUser(null);
        setError(t('loginModalOtpInvalid') || 'Kirish ma’lumotlari noto‘g‘ri.');
        return;
      }
      const normalized = normalizeUser(user);
      clearAdminImpersonationBackup();
      setEffectiveRole(null, null);
      setUser(normalized);
      const needsPin =
        (normalized.roleSwitchPinSet === false || normalized.mustSetRoleSwitchPin === true) &&
        (normalized.role === 'tenant_owner' || normalized.role === 'cashier');
      if (needsPin) {
        setStep('set_pin');
        return;
      }
      navigate('/admin');
      onClose();
    },
    [navigate, onClose, setUser, setEffectiveRole, t],
  );

  // ─── Step: phone — password login + send 2FA OTP ──────────────────────────
  const handlePasswordLogin = async () => {
    if (phoneDigits.length !== 9) {
      setError(t('loginModalPhoneError'));
      return;
    }
    if (!fullPhone || !normalizeLoginPhone(fullPhone)) {
      setError(t('loginModalAuth_phone_invalid'));
      return;
    }
    if (!password || String(password).length < 4) {
      setError(t('loginModalPasswordError'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const direct = await loginWithPassword(fullPhone, password);
      if (direct?.requiresOtp) {
        await sendLoginOtp(fullPhone, password);
        setLoginOtp('');
        setResendKey((k) => k + 1);
        setStep('otp_login');
        return;
      }
      if (direct?.token || direct?.accessToken) {
        finalizeLogin(direct);
        return;
      }
      await sendLoginOtp(fullPhone, password);
      setLoginOtp('');
      setResendKey((k) => k + 1);
      setStep('otp_login');
    } catch (err) {
      const code =
        err?.code ||
        err?.response?.data?.code ||
        err?.response?.data?.meta?.auth_reason ||
        err?.response?.data?.meta?.authReason;
      if (code === 'invalid_credentials') {
        setError(t('loginModalAuth_invalid_credentials'));
        return;
      }
      if (code === 'phone_invalid') {
        setError(t('loginModalAuth_phone_invalid'));
        return;
      }
      if (code === 'missing_or_invalid_fields') {
        setError(t('loginModalAuth_missing_or_invalid_fields'));
        return;
      }
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage ?? err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  };

  // ─── Step: otp_login — verify OTP and complete login ──────────────────────
  const handleVerifyLoginOtp = async (codeOverride) => {
    const code = (codeOverride ?? loginOtp).replace(/\D/g, '').slice(0, OTP_CODE_LENGTH);
    if (code.length !== OTP_CODE_LENGTH) {
      setError(t('loginModalOtpError'));
      return;
    }
    if (code !== loginOtp) {
      setLoginOtp(code);
    }
    setError('');
    setLoading(true);
    try {
      const result = await verifyLogin(fullPhone, code);
      if (!result?.token && !result?.accessToken) {
        setError(t('loginModalOtpInvalid'));
        return;
      }
      finalizeLogin(result);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'OTP_INVALID' || code === 'OTP_EXPIRED' || code === 'OTP_NOT_FOUND') {
        setError(t('loginModalOtpInvalid'));
        return;
      }
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage ?? err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendLoginOtp = async () => {
    if (phoneDigits.length !== 9 || !password) return;
    try {
      await sendLoginOtp(fullPhone, password);
      setLoginOtp('');
      setError('');
    } catch {
      // silent — cooldown timer will reset automatically
    }
  };

  // ─── Step: reset_send — send reset OTP ────────────────────────────────────
  const handleSendResetOtp = async () => {
    if (phoneDigits.length !== 9) {
      setError(t('loginModalPhoneError'));
      return;
    }
    if (!fullPhone || !normalizeLoginPhone(fullPhone)) {
      setError(t('loginModalAuth_phone_invalid'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await sendResetPasswordOtp(fullPhone);
      setResetOtp('');
      setResendKey((k) => k + 1);
      setStep('reset_otp');
    } catch (err) {
      const code = err?.code || err?.response?.data?.code;
      if (code === 'phone_invalid' || code === 'PHONE_INVALID') {
        setError(t('loginModalAuth_phone_invalid'));
        return;
      }
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage ?? err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetOtp = async () => {
    if (phoneDigits.length !== 9) return;
    try {
      await sendResetPasswordOtp(fullPhone);
      setResetOtp('');
    } catch {
      // silent
    }
  };

  // ─── Step: reset_otp → confirm OTP, go to new password step ──────────────
  const handleConfirmResetOtp = (codeOverride) => {
    const code = (codeOverride ?? resetOtp).replace(/\D/g, '').slice(0, OTP_CODE_LENGTH);
    if (code.length !== OTP_CODE_LENGTH) {
      setError(t('loginModalOtpError'));
      return;
    }
    if (code !== resetOtp) {
      setResetOtp(code);
    }
    setError('');
    setStep('reset_newpass');
  };

  // ─── Step: reset_newpass — save new password ──────────────────────────────
  const handleSaveNewPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError(t('loginModalPasswordMinError'));
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError(t('loginModalPasswordMismatch'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await confirmResetPassword({
        phone: fullPhone,
        code: resetOtp,
        newPassword,
        newPasswordConfirm,
      });
      setResetSuccess(true);
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === 'OTP_INVALID' || code === 'OTP_EXPIRED') {
        setError(t('loginModalOtpInvalid'));
        setStep('reset_otp');
        return;
      }
      if (code === 'PASSWORD_MISMATCH') {
        setError(t('loginModalPasswordMismatch'));
        return;
      }
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage ?? err?.message ?? String(err));
    } finally {
      setLoading(false);
    }
  };

  // ─── Step: set_pin ────────────────────────────────────────────────────────
  const handleSetPin = async () => {
    const p = pinVal.trim();
    const c = pinConfirmVal.trim();
    if (p.length < 4) {
      setPinError(t('roleSwitch_pin_min') || 'PIN kamida 4 ta raqam bo\'lishi kerak');
      return;
    }
    if (p !== c) {
      setPinError(t('roleSwitch_pin_mismatch') || 'PIN mos emas');
      return;
    }
    setPinError('');
    setLoading(true);
    try {
      await updateRoleSwitchPin({ newPin: p, newPinConfirm: c });
      navigate('/admin');
      onClose();
    } catch (err) {
      setPinError(err?.response?.data?.message ?? err?.message ?? 'PIN saqlashda xatolik');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (isPageMode) return;
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen && !isPageMode) return null;

  const isSmsStep = step === 'otp_login' || step === 'reset_otp';
  const overlayClass = isPageMode
    ? 'login-modal-page'
    : `login-modal-overlay${isSmsStep ? ' login-modal-overlay--sms' : ''}`;

  return (
    <div
      className={overlayClass}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
    >
      {isPageMode && (
        <aside className="login-modal-page-hero" aria-hidden="true">
          <div className="login-modal-page-brand">
            <LoginHeroBrandLogo />
          </div>
          <h1>Biznes boshqaruvi bitta joyda</h1>
          <p>Ombor, kassa, filiallar va hisobotlar desktop ilovada tez va barqaror ishlaydi.</p>
          <div className="login-modal-page-metrics">
            <div><b>POS</b><span>tezkor savdo</span></div>
            <div><b>Ombor</b><span>real qoldiq</span></div>
            <div><b>Printer</b><span>avto ulanish</span></div>
          </div>
        </aside>
      )}
      <div className={`login-modal ${isPageMode ? 'login-modal--page' : ''}`} onClick={(e) => e.stopPropagation()}>

        {/* Close button */}
        {!isPageMode && (
          <button
            type="button"
            className="login-modal-close"
            onClick={onClose}
            aria-label={t('loginModalClose')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* ── Step: phone (login form) ────────────────────────────────────── */}
        {step === 'phone' && (
          <>
            <h2 id="login-modal-title" className="login-modal-title">{t('loginModalTitle')}</h2>
            <p className="login-modal-subtitle">{t('loginModalPasswordSubtitle')}</p>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="login-phone">{t('loginModalPhoneLabel')}</label>
              <div className="login-modal-phone-wrap">
                <span className="login-modal-prefix">{PHONE_PREFIX}</span>
                <input
                  id="login-phone"
                  type="tel"
                  className="login-modal-input"
                  placeholder={t('loginModalPhonePlaceholder')}
                  value={formatPhoneWithSpaces(phone)}
                  onChange={handlePhoneChange}
                  autoComplete="tel"
                  autoFocus
                />
              </div>
            </div>

            <div className="login-modal-field">
              <div className="login-modal-label-row">
                <label className="login-modal-label" htmlFor="login-password">{t('loginModalPasswordLabel')}</label>
                <button
                  type="button"
                  className="login-modal-forgot-btn"
                  onClick={() => {
                    setError('');
                    setResetOtp('');
                    setNewPassword('');
                    setNewPasswordConfirm('');
                    setResetSuccess(false);
                    setStep('reset_send');
                  }}
                >
                  {t('loginModalForgotPassword')}
                </button>
              </div>
              <div className="login-modal-password-wrap">
                <input
                  id="login-password"
                  type={passwordVisible ? 'text' : 'password'}
                  className="login-modal-input login-modal-input--password"
                  placeholder={t('loginModalPasswordPlaceholder')}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handlePasswordLogin(); }}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-modal-password-toggle"
                  onClick={() => setPasswordVisible((v) => !v)}
                  aria-label={passwordVisible ? (t('loginModalPasswordHide') || 'Parolni yashirish') : (t('loginModalPasswordShow') || 'Parolni ko\'rsatish')}
                  tabIndex={-1}
                >
                  {passwordVisible ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M3 3l18 18M10.58 10.58A2 2 0 0 0 12 15a2 2 0 0 0 1.42-.58M9.88 5.09A10.94 10.94 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.17M6.11 6.11A13.3 13.3 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.11-1.44" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && <p className="login-modal-error">{error}</p>}

            <button
              type="button"
              className="login-modal-btn login-modal-btn-primary"
              onClick={handlePasswordLogin}
              disabled={loading}
            >
              {loading ? t('loginModalSigningIn') : t('loginModalSignIn')}
            </button>
          </>
        )}

        {/* ── Step: otp_login (2FA verification) ─────────────────────────── */}
        {step === 'otp_login' && (
          <>
            <div className="login-modal-step-icon login-modal-step-icon--sms" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M8 10h8M8 14h5M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 id="login-modal-title" className="login-modal-title">{t('loginModal2FATitle')}</h2>
            <p className="login-modal-subtitle">{t('loginModal2FASubtitle')}</p>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="otp-login">{t('loginModalOtpLabel')}</label>
              <OtpInput
                id="otp-login"
                value={loginOtp}
                onChange={(v) => { setLoginOtp(v); setError(''); }}
                disabled={loading}
                placeholder={t('loginModalOtpPlaceholder')}
                webOtpActive
                onComplete={(code) => { if (!loading) handleVerifyLoginOtp(code); }}
              />
            </div>

            {error && <p className="login-modal-error">{error}</p>}

            <button
              type="button"
              className="login-modal-btn login-modal-btn-primary"
              onClick={handleVerifyLoginOtp}
              disabled={loading || loginOtp.length !== OTP_CODE_LENGTH}
            >
              {loading ? t('loginModalChecking') : t('loginModalConfirm')}
            </button>

            <div className="login-modal-footer-row">
              <button
                type="button"
                className="login-modal-back-btn"
                onClick={() => { setStep('phone'); setError(''); setLoginOtp(''); }}
                disabled={loading}
              >
                ← {t('loginModalBack')}
              </button>
              <ResendTimer
                key={resendKey}
                cooldown={OTP_RESEND_COOLDOWN}
                onResend={handleResendLoginOtp}
                loading={loading}
                label={t('loginModalResend')}
                labelTimer={t('loginModalResendIn')}
              />
            </div>
          </>
        )}

        {/* ── Step: reset_send (enter phone, send reset OTP) ─────────────── */}
        {step === 'reset_send' && (
          <>
            <div className="login-modal-step-icon login-modal-step-icon--reset" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-6v-4m0-4h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 id="login-modal-title" className="login-modal-title">{t('loginModalResetTitle')}</h2>
            <p className="login-modal-subtitle">{t('loginModalResetSubtitle')}</p>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="reset-phone">{t('loginModalPhoneLabel')}</label>
              <div className="login-modal-phone-wrap">
                <span className="login-modal-prefix">{PHONE_PREFIX}</span>
                <input
                  id="reset-phone"
                  type="tel"
                  className="login-modal-input"
                  placeholder={t('loginModalPhonePlaceholder')}
                  value={formatPhoneWithSpaces(phone)}
                  onChange={handlePhoneChange}
                  autoComplete="tel"
                  autoFocus
                />
              </div>
            </div>

            {error && <p className="login-modal-error">{error}</p>}

            <button
              type="button"
              className="login-modal-btn login-modal-btn-primary"
              onClick={handleSendResetOtp}
              disabled={loading || phoneDigits.length !== 9}
            >
              {loading ? t('loginModalSending') : t('loginModalResetSendCode')}
            </button>

            <button
              type="button"
              className="login-modal-btn login-modal-btn-ghost"
              onClick={() => { setStep('phone'); setError(''); }}
              disabled={loading}
            >
              ← {t('loginModalBack')}
            </button>
          </>
        )}

        {/* ── Step: reset_otp (enter OTP for password reset) ─────────────── */}
        {step === 'reset_otp' && (
          <>
            <div className="login-modal-step-icon login-modal-step-icon--sms" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M8 10h8M8 14h5M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 id="login-modal-title" className="login-modal-title">{t('loginModalResetTitle')}</h2>
            <p className="login-modal-subtitle">{t('loginModalResetOtpSubtitle')}</p>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="otp-reset">{t('loginModalOtpLabel')}</label>
              <OtpInput
                id="otp-reset"
                value={resetOtp}
                onChange={(v) => { setResetOtp(v); setError(''); }}
                disabled={loading}
                placeholder={t('loginModalOtpPlaceholder')}
                webOtpActive
                onComplete={(code) => { if (!loading) handleConfirmResetOtp(code); }}
              />
            </div>

            {error && <p className="login-modal-error">{error}</p>}

            <button
              type="button"
              className="login-modal-btn login-modal-btn-primary"
              onClick={handleConfirmResetOtp}
              disabled={loading || resetOtp.length !== OTP_CODE_LENGTH}
            >
              {t('loginModalConfirm')}
            </button>

            <div className="login-modal-footer-row">
              <button
                type="button"
                className="login-modal-back-btn"
                onClick={() => { setStep('reset_send'); setError(''); setResetOtp(''); }}
                disabled={loading}
              >
                ← {t('loginModalBack')}
              </button>
              <ResendTimer
                key={resendKey}
                cooldown={OTP_RESEND_COOLDOWN}
                onResend={handleResendResetOtp}
                loading={loading}
                label={t('loginModalResend')}
                labelTimer={t('loginModalResendIn')}
              />
            </div>
          </>
        )}

        {/* ── Step: reset_newpass (set new password) ──────────────────────── */}
        {step === 'reset_newpass' && !resetSuccess && (
          <>
            <div className="login-modal-step-icon login-modal-step-icon--lock" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1.5" fill="currentColor" />
              </svg>
            </div>
            <h2 id="login-modal-title" className="login-modal-title">{t('loginModalNewPasswordTitle')}</h2>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="new-password">{t('loginModalNewPasswordLabel')}</label>
              <input
                id="new-password"
                type="password"
                className="login-modal-input"
                placeholder={t('loginModalNewPasswordPlaceholder')}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                autoComplete="new-password"
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="new-password-confirm">{t('loginModalNewPasswordConfirmLabel')}</label>
              <input
                id="new-password-confirm"
                type="password"
                className="login-modal-input"
                placeholder={t('loginModalNewPasswordConfirmPlaceholder')}
                value={newPasswordConfirm}
                onChange={(e) => { setNewPasswordConfirm(e.target.value); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNewPassword(); }}
                autoComplete="new-password"
                disabled={loading}
              />
            </div>

            {error && <p className="login-modal-error">{error}</p>}

            <button
              type="button"
              className="login-modal-btn login-modal-btn-primary"
              onClick={handleSaveNewPassword}
              disabled={loading || !newPassword || !newPasswordConfirm}
            >
              {loading ? t('loginModalSaving') : t('loginModalSavePassword')}
            </button>
          </>
        )}

        {/* ── Reset success ───────────────────────────────────────────────── */}
        {step === 'reset_newpass' && resetSuccess && (
          <>
            <div className="login-modal-step-icon login-modal-step-icon--success" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
                <path d="M7 12l3.5 3.5L17 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 id="login-modal-title" className="login-modal-title login-modal-title--success">
              {t('loginModalSuccessTitle')}
            </h2>
            <p className="login-modal-subtitle login-modal-subtitle--success">{t('loginModalResetSuccess')}</p>
            <button
              type="button"
              className="login-modal-btn login-modal-btn-primary"
              onClick={() => {
                setStep('phone');
                setPassword('');
                setResetOtp('');
                setNewPassword('');
                setNewPasswordConfirm('');
                setResetSuccess(false);
                setError('');
              }}
            >
              {t('loginModalSignIn')}
            </button>
          </>
        )}

        {/* ── Step: set_pin ───────────────────────────────────────────────── */}
        {step === 'set_pin' && (
          <>
            <h2 id="login-modal-title" className="login-modal-title">
              {t('roleSwitch_set_pin') || 'Rol almashtirish PIN\'ini o\'rnating'}
            </h2>
            <p className="login-modal-subtitle">
              {t('roleSwitch_set_pin_first') || 'Kassir va tenant_owner o\'rtasida rol almashtirish uchun PIN kerak.'}
            </p>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="login-pin-new">
                {t('roleSwitch_pin_new') || 'Yangi PIN (kamida 4 ta raqam)'}
              </label>
              <input
                id="login-pin-new"
                type="password"
                inputMode="numeric"
                maxLength={8}
                className="login-modal-input"
                placeholder="••••"
                value={pinVal}
                onChange={(e) => { setPinVal(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinError(''); }}
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="login-modal-field">
              <label className="login-modal-label" htmlFor="login-pin-confirm">
                {t('roleSwitch_pin_confirm') || 'PIN ni tasdiqlang'}
              </label>
              <input
                id="login-pin-confirm"
                type="password"
                inputMode="numeric"
                maxLength={8}
                className="login-modal-input"
                placeholder="••••"
                value={pinConfirmVal}
                onChange={(e) => { setPinConfirmVal(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinError(''); }}
                disabled={loading}
              />
            </div>

            {pinError && <p className="login-modal-error">{pinError}</p>}

            <div className="login-modal-actions">
              <button
                type="button"
                className="login-modal-btn login-modal-btn-secondary"
                onClick={() => { navigate('/admin'); onClose(); }}
                disabled={loading}
              >
                {t('adminBranch_cancel') || 'Keyinroq'}
              </button>
              <button
                type="button"
                className="login-modal-btn login-modal-btn-primary"
                onClick={handleSetPin}
                disabled={loading}
              >
                {loading ? (t('loginModalChecking') || 'Saqlanmoqda...') : (t('roleSwitch_set_pin') || 'PIN saqlash')}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default LoginModal;
